import type { WidgetConfig } from "@anota/shared";
import { LAUNCHER_STYLES, type LauncherStyle } from "@anota/shared/limits";
import { render } from "preact";
import type { Backend } from "./backend";
import { pickLang } from "./i18n";
import { App, HOST_ID, type Controls } from "./ui/App";
import { CSS } from "./ui/styles";

export interface MountOptions {
  config: WidgetConfig;
  backend: Backend;
  /** Idioma pedido por el sitio (data-lang u opción del paquete); si no, el del proyecto o el del navegador. */
  lang?: string;
  /** Nonce de la CSP del sitio, para el <style> de respaldo. */
  nonce?: string;
  /** Estilo pedido por el sitio (data-launcher u opción del paquete); si no, el del proyecto. */
  launcher?: string;
}

export interface Mounted extends Controls {
  destroy: () => void;
}

/**
 * Monta el widget dentro de un Shadow DOM colgado de <html> (no de <body>: hay frameworks que reemplazan
 * el body al navegar). Llamar con el DOM ya cargado. Lo usan el `<script>` y el paquete npm.
 */
export function mount({ config: base, backend, lang, nonce, launcher }: MountOptions): Mounted {
  const config = launcher && (LAUNCHER_STYLES as readonly string[]).includes(launcher) ? { ...base, settings: { ...base.settings, launcher: launcher as LauncherStyle } } : base;
  const host = document.createElement("div");
  host.id = HOST_ID;
  host.style.cssText = "all:initial;position:fixed;top:0;left:0;width:0;height:0;z-index:2147483647;";
  document.documentElement.appendChild(host);
  const shadow = host.attachShadow({ mode: "open" });
  // Hoja construida: no la bloquea una CSP estricta con style-src (un <style> sí).
  if ("adoptedStyleSheets" in shadow && typeof CSSStyleSheet !== "undefined" && "replaceSync" in CSSStyleSheet.prototype) {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(CSS);
    shadow.adoptedStyleSheets = [sheet];
  } else {
    const style = document.createElement("style");
    if (nonce) style.nonce = nonce;
    style.textContent = CSS;
    shadow.appendChild(style);
  }
  const root = document.createElement("div");
  shadow.appendChild(root);

  let controls: Controls | null = null;
  render(<App config={config} backend={backend} host={host} lang={pickLang(lang, config.settings.language)} bind={(c) => (controls = c)} />, root);
  return {
    open: () => controls?.open(),
    openNotes: () => controls?.openNotes(),
    destroy: () => {
      render(null, root);
      host.remove();
    },
  };
}
